import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Check, Crown, CreditCard, Shield, Tag, Mail
} from 'lucide-react';

import { safeArray } from '../lib/utils';
import AdmissionCounter from '../components/AdmissionCounter';
import ShareEarnSection from '../components/ShareEarnSection';
import PaymentLoader from '../components/PaymentLoader';
import SEO from '../components/SEO';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useCountryPricing } from '../hooks/useCountryPricing';
import PricingPlanCard, { formatPlanPrice, defaultPlanId } from '../components/PricingPlanCard';
import LegalAcceptCheckbox, { recordLegalAcceptance } from '../components/LegalAcceptCheckbox';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const getErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map((e) => e.msg).join(', ') || fallback;
  if (data.detail?.message) return data.detail.message;
  if (data.message) return data.message;
  return fallback;
};

const FAQ_ITEMS = [
  {
    q: 'Is Premium Access really permanent?',
    a: 'Yes — pay once and you have access to every course, forever. No renewals, no recurring charges, no expiry date.',
  },
  {
    q: "What's included?",
    a: 'All current courses, all future courses and updates we release, certificates on completion, the mobile app, and full community access.',
  },
  {
    q: 'Are there any recurring fees?',
    a: 'No. This is a single one-time payment. You will never be charged again for continued access.',
  },
  {
    q: 'What payment methods are accepted?',
    a: 'We accept all major credit/debit cards via Stripe, as well as PayPal.',
  },
  {
    q: 'Is GST included in the price shown?',
    a: 'The headline price is exclusive of GST. GST is added at checkout — the final GST-inclusive amount is shown before you pay.',
  },
];

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token, isPremium, refreshUser } = useAuth();
  const { t, tFormat } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupons, setAppliedCoupons] = useState([]);
  const [couponResult, setCouponResult] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [paymentLoader, setPaymentLoader] = useState({ visible: false, status: 'processing', message: '' });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [admissionInfo, setAdmissionInfo] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  // Dated subscription plans + Lifetime for the current country (GET /settings/plans,
  // backend/utils/subscriptions.py). Empty / Lifetime-only → page behaves as before.
  const [plans, setPlans] = useState([]);
  // null until plans load; ?plan=<id> (e.g. from the home page's pricing
  // section) preselects a tab.
  const [selectedPlanId, setSelectedPlanId] = useState(
    () => new URLSearchParams(window.location.search).get('plan')
  );

  // Country-aware pricing (IP-detected, 24h cached, user-overridable) — see
  // frontend/src/hooks/useCountryPricing.js and routes/country_pricing.py.
  // India keeps sourcing its price/GST from `settings` (type: platform) below,
  // unchanged from before country pricing existed — only non-India countries
  // read from this hook, so nothing about the existing INR flow moves.
  const { pricing: countryPricing, countryCode, setCountry: setCountryCode } = useCountryPricing();
  const isIndia = countryCode === 'IN';

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (!countryCode) return;
    axios.get(`${API_URL}/settings/plans`, { params: { country_code: countryCode } })
      .then(({ data }) => {
        const list = safeArray(data?.plans);
        setPlans(list);
        setSelectedPlanId((prev) => defaultPlanId(list, prev));
      })
      .catch(() => setPlans([]));
  }, [countryCode]);

  // Coupons are validated against one plan's price — re-pick them after switching plans.
  useEffect(() => {
    setAppliedCoupons([]);
    setCouponResult(null);
  }, [selectedPlanId]);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: 'Pricing Page',
        content_type: 'product'
      });
    }
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, admissionRes, gatewayRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/settings`),
        axios.get(`${API_URL}/admissions/count`).catch(() => null),
        axios.get(`${API_URL}/settings/payment-gateways/public`).catch(() => null),
        axios.get(`${API_URL}/country-pricing`).catch(() => null)
      ]);
      setSettings(
        settingsRes.data && typeof settingsRes.data === 'object' ? settingsRes.data : null
      );
      if (admissionRes?.data) setAdmissionInfo(admissionRes.data);
      if (gatewayRes?.data) setGatewayConfig(gatewayRes.data);
      if (Array.isArray(countriesRes?.data)) setAvailableCountries(countriesRes.data);

      // Fetch subscription status if logged in
      if (token) {
        try {
          const statusRes = await axios.get(`${API_URL}/payments/subscription/status`, { headers });
          setSubscriptionStatus(statusRes.data);
        } catch (err) {}
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyCoupons = async (codes) => {
    const { data } = await axios.post(
      `${API_URL}/payments/coupons/validate-multiple`,
      { coupon_codes: codes, plan_type: planId },
      { headers }
    );
    setAppliedCoupons(codes);
    setCouponResult(data);
  };

  const handleAddCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (appliedCoupons.includes(code)) {
      toast.error(t('pricing_coupon_already_applied', 'This coupon is already applied'));
      return;
    }
    if (appliedCoupons.length >= 2) {
      toast.error(t('pricing_coupon_max_two', 'Maximum 2 coupons can be applied'));
      return;
    }

    setValidatingCoupon(true);
    try {
      await applyCoupons([...appliedCoupons, code]);
      setCouponInput('');
      toast.success(tFormat('pricing_coupon_applied_short', { code }, '✓ {code} applied!'));
    } catch (err) {
      toast.error(
        getErrorMessage(err, t('pricing_invalid_coupon', 'Invalid coupon'))
      );
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = async (codeToRemove) => {
    const newCodes = appliedCoupons.filter((c) => c !== codeToRemove);
    if (newCodes.length === 0) {
      setAppliedCoupons([]);
      setCouponResult(null);
      return;
    }
    try {
      await applyCoupons(newCodes);
    } catch (err) {
      // remaining coupon(s) somehow no longer valid together — clear and let the user retry
      setAppliedCoupons([]);
      setCouponResult(null);
      toast.error(
        getErrorMessage(err, t('pricing_coupon_validate_fail', 'Failed to validate coupon'))
      );
    }
  };

  // Every checkout requires the Terms/Refund checkbox; logged-in users' acceptance
  // is recorded server-side before a gateway is opened (signup records its own).
  const ensureTermsAccepted = () => {
    if (acceptedTerms) return true;
    setTermsError(true);
    toast.error(t('legal_accept_required', 'Please accept the Terms & Conditions and Refund Policy to continue.'));
    return false;
  };

  const handlePayment = async (paymentMethod) => {
    if (!ensureTermsAccepted()) return;
    if (!user) {
      navigate('/signup', { state: { from: { pathname: '/pricing' }, plan: planId } });
      return;
    }

    setProcessingPayment(true);
    try {
      await recordLegalAcceptance(headers, planId);
      const originUrl = window.location.origin;
      const currencyCode = isIndia ? 'INR' : (countryPricing?.currency_code || 'INR');

      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          value: gstInclusiveTotal,
          currency: currencyCode,
          content_name: 'Thachari Premium Access'
        });
      }
      // Stripe/PayPal redirect away from this page, so the checkout amount is
      // stashed here and read back by ThankYou once the browser returns —
      // Razorpay stays on-page and fires its own Purchase event directly instead.
      sessionStorage.setItem(
        'thachari_purchase_meta',
        JSON.stringify({ value: gstInclusiveTotal, currency: currencyCode })
      );

      // The stacked-coupon discount *preview* is India-only (see the coupon
      // block's comment above) — but Stripe/PayPal checkout itself validates
      // and applies a coupon against each country's own base price server-side
      // (routes/payments.py), so non-India codes are still passed through here,
      // just without a pre-checkout discount preview.
      const couponCode = isIndia
        ? (appliedCoupons[0] || null)
        : (couponInput.trim().toUpperCase() || null);

      if (paymentMethod === 'stripe') {
        const res = await axios.post(`${API_URL}/payments/stripe/create-session`, {
          plan_type: planId,
          coupon_code: couponCode,
          origin_url: originUrl,
          country_code: countryCode
        }, { headers });

        window.location.href = res.data.url;
      } else if (paymentMethod === 'paypal') {
        const res = await axios.post(`${API_URL}/payments/paypal/create-order`, {
          plan_type: planId,
          coupon_code: couponCode,
          origin_url: originUrl,
          country_code: countryCode
        }, { headers });

        toast.info(t('pricing_toast_paypal_redirect', 'PayPal order created. Redirecting...'));
        if (res.data.approval_url) {
          window.location.href = res.data.approval_url;
        }
      }
    } catch (err) {
      toast.error(
        getErrorMessage(err, t('pricing_toast_payment_fail', 'Payment failed. Please try again.'))
      );
    } finally {
      setProcessingPayment(false);
    }
  };

  const showPaymentLoader = (status, message = '') => {
    setPaymentLoader({ visible: true, status, message });
  };

  const hidePaymentLoader = () => {
    setPaymentLoader({ visible: false, status: 'processing', message: '' });
  };

  const handleRazorpayPayment = async () => {
    if (!ensureTermsAccepted()) return;
    if (!user) {
      navigate('/signup', { state: { from: { pathname: '/pricing' }, plan: planId } });
      return;
    }

    setProcessingPayment(true);
    showPaymentLoader('processing', 'Setting up your payment...');
    const currencyCode = isIndia ? 'INR' : (countryPricing?.currency_code || 'INR');
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: gstInclusiveTotal,
        currency: currencyCode,
        content_name: 'Thachari Premium Access'
      });
    }
    try {
      await recordLegalAcceptance(headers, planId);
      const { data: order } = isDatedPlan
        ? await axios.post(
          `${API_URL}/payments/razorpay/subscribe`,
          { plan_id: planId, coupon_codes: appliedCoupons, country_code: countryCode },
          { headers }
        )
        : await axios.post(
          `${API_URL}/payments/razorpay/create-order`,
          { plan_type: 'lifetime', coupon_codes: appliedCoupons },
          { headers }
        );

      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
          document.body.appendChild(script);
        });
      }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Thachari',
        description: isDatedPlan ? `Premium Access — ${selectedPlan.name}` : 'Premium Access',
        order_id: order.order_id,
        prefill: {
          name: user?.name,
          email: user?.email,
          contact: user?.phone ? `${user.phone_country_code || '+91'}${user.phone}` : undefined
        },
        theme: { color: '#A67C52' },
        handler: async (response) => {
          showPaymentLoader('verifying', 'Confirming your payment securely...');
          try {
            await axios.post(
              `${API_URL}/payments/razorpay/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers }
            );
            if (window.fbq) {
              window.fbq('track', 'Purchase', {
                value: gstInclusiveTotal,
                currency: currencyCode,
                content_name: 'Thachari Premium Access'
              });
            }
            showPaymentLoader('success', 'Premium access activated! Redirecting...');
            setTimeout(async () => {
              hidePaymentLoader();
              setProcessingPayment(false);
              await refreshUser?.();
              navigate('/thank-you?gateway=razorpay');
            }, 2000);
          } catch (err) {
            showPaymentLoader(
              'failed',
              t('pricing_toast_razorpay_verify_fail', 'Payment verification failed. Contact support.')
            );
            setProcessingPayment(false);
          }
        },
        modal: {
          // User dismissed the Razorpay modal without paying
          ondismiss: () => {
            hidePaymentLoader();
            setProcessingPayment(false);
          }
        }
      };

      // Hide our "setting up" loader right before opening Razorpay
      // so its own modal opens on a clean screen
      hidePaymentLoader();

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        showPaymentLoader(
          'failed',
          tFormat(
            'pricing_toast_razorpay_failed',
            { reason: response.error?.description || '' },
            'Payment failed: {reason}'
          )
        );
        setProcessingPayment(false);
      });
      rzp.open();
    } catch (err) {
      showPaymentLoader(
        'failed',
        getErrorMessage(err, t('pricing_toast_payment_fail', 'Could not initiate payment. Please try again.'))
      );
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      !confirm(
        t(
          'pricing_confirm_cancel',
          'Are you sure you want to cancel? You will retain access until your current period ends.'
        )
      )
    ) {
      return;
    }

    try {
      await axios.post(`${API_URL}/payments/subscription/cancel`, {}, { headers });
      toast.success(t('pricing_toast_cancel_ok', 'Subscription cancelled successfully'));
      fetchData();
    } catch (err) {
      toast.error(t('pricing_toast_cancel_fail', 'Failed to cancel subscription'));
    }
  };

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistSubmitting(true);
    try {
      await axios.post(`${API_URL}/admissions/waitlist`, { email: waitlistEmail.trim() });
      setWaitlistJoined(true);
      toast.success("You're on the waitlist! We'll notify you when a seat opens up.");
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to join waitlist'));
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  // India formats with Intl's INR currency formatter (unchanged from before country
  // pricing existed); every other country just prefixes its currency_symbol — Intl's
  // currency formatter needs a real ISO code per-locale mapping we don't have for
  // arbitrary admin-entered currencies, and the symbol prefix matches what the
  // /geo/country and /country-pricing endpoints already hand back as display_price.
  const formatPrice = (price) => formatPlanPrice(price, { isIndia, symbol: countryPricing?.currency_symbol || '' });

  // A dated plan (monthly/quarterly/...) overrides the Lifetime figures below;
  // Lifetime keeps its pre-existing sources so that flow is unchanged.
  const datedPlans = plans.filter((p) => p.id !== 'lifetime');
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  const isDatedPlan = !!selectedPlan && selectedPlan.id !== 'lifetime';
  // What checkout sends — Lifetime until plans have loaded / if none match.
  const planId = selectedPlan?.id || 'lifetime';

  const lifetimePrice = isDatedPlan
    ? Number(selectedPlan.base_price)
    : isIndia
      ? Number(settings?.lifetime_price ?? 4999)
      : Number(countryPricing?.base_price ?? settings?.lifetime_price ?? 4999);
  const lifetimeOriginalPrice = isDatedPlan
    ? Number(selectedPlan.original_price)
    : isIndia
      ? Number(settings?.lifetime_original_price ?? 9999)
      : Number(countryPricing?.original_price ?? settings?.lifetime_original_price ?? 9999);
  const gstPercentage = isDatedPlan
    ? Number(selectedPlan.tax_rate)
    : isIndia
      ? Number(settings?.gst_percentage ?? 18)
      : Number(countryPricing?.tax_rate ?? 18);
  const taxLabel = isDatedPlan
    ? selectedPlan.tax_label
    : isIndia ? 'GST' : (countryPricing?.tax_label || 'Tax');
  // couponResult.price_after_discount (from POST /payments/coupons/validate-multiple,
  // India-only — see the coupon block below) is the pre-tax price after all stacked
  // coupons' combined discount; total_with_gst is the tax-inclusive figure every
  // gateway actually charges.
  const finalPrice = (isIndia && couponResult?.valid)
    ? couponResult.price_after_discount
    : lifetimePrice;
  const hasDiscount = isIndia && couponResult?.valid && couponResult.total_discount > 0;
  const gstInclusiveTotal = (isIndia && couponResult?.valid)
    ? Math.round(couponResult.total_with_gst)
    : Math.round(finalPrice * (1 + gstPercentage / 100));
  // Country switcher only offers countries with their own configured pricing —
  // everyone else stays on the IP-detected/default country.
  const switchableCountries = availableCountries.filter((c) => c.enabled);
  const seatsLow = admissionInfo && admissionInfo.remaining > 0 && admissionInfo.remaining < 200;
  const admissionsClosed = admissionInfo && admissionInfo.remaining <= 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted w-1/3 mx-auto rounded" />
              <div className="h-96 bg-muted rounded-xl max-w-lg mx-auto" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Premium user view with subscription status
  if (isPremium && subscriptionStatus) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-amber-500" />
              </div>
              <h1 className="text-4xl font-serif font-medium mb-4">
                {t('pricing_member_title', 'Premium Member')}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t(
                  'pricing_member_subtitle',
                  'You have full access to all courses and features.'
                )}
              </p>
            </div>

            <Card className="mb-8">
              <CardContent className="pt-6">
                {subscriptionStatus.is_lifetime ? (
                  <div className="text-center py-2">
                    <Badge className="mb-2 bg-[#A67C52] hover:bg-[#A67C52] text-white">
                      {t('pricing_lifetime_badge', 'Premium Access')}
                    </Badge>
                    <p className="text-lg font-semibold">
                      {t('pricing_lifetime_never_expires', 'Premium Access — Active ✓ Never Expires')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('pricing_current_plan', 'Current Plan')}
                      </p>
                      <p className="text-lg font-semibold capitalize">{subscriptionStatus.plan}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('pricing_days_remaining', 'Days Remaining')}
                      </p>
                      <p className="text-lg font-semibold">
                        {subscriptionStatus.days_remaining}{' '}
                        {t('pricing_days_suffix', 'days')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('pricing_expires_on', 'Expires On')}
                      </p>
                      <p className="text-lg font-semibold">
                        {new Date(subscriptionStatus.expiry_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {t('pricing_status', 'Status')}
                      </p>
                      <Badge variant={subscriptionStatus.is_cancelled ? "destructive" : "default"}>
                        {subscriptionStatus.is_cancelled
                          ? t('pricing_status_cancelled', 'Cancelled')
                          : t('pricing_status_active', 'Active')}
                      </Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/courses')} className="rounded-full">
                {t('pricing_browse_courses', 'Browse Courses')}
              </Button>
              {!subscriptionStatus.is_lifetime && !subscriptionStatus.is_cancelled && (
                <Button
                  variant="outline"
                  onClick={handleCancelSubscription}
                  className="rounded-full"
                >
                  {t('pricing_cancel_sub', 'Cancel Subscription')}
                </Button>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <SEO
        title="Pricing — Premium Access to All Interior Courses"
        description="Get lifetime access to all interior technician, carpentry and execution courses. One-time payment."
        keywords="interior technician course fee, interior course price, interior training cost India, online interior course India"
        url="/pricing"
      />
      <PaymentLoader
        visible={paymentLoader.visible}
        status={paymentLoader.status}
        message={paymentLoader.message}
      />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 px-4 py-1">{t('pricing_badge', 'Pricing')}</Badge>
            <h1 className="text-4xl md:text-5xl font-serif font-medium mb-4">
              {datedPlans.length > 0
                ? t('pricing_main_title_plans', 'Choose Your Plan')
                : t('pricing_main_title', 'One Plan. Premium Access.')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {datedPlans.length > 0
                ? t(
                  'pricing_main_subtitle_plans',
                  'Full access to all courses, certificates and the mobile app. Pay for 1, 3, 6 or 12 months — no auto-renewal — or once for lifetime access.'
                )
                : t(
                  'pricing_main_subtitle',
                  'Pay once, learn forever. Every course, every future update, no recurring fees.'
                )}
            </p>
          </div>

          {/* Admission Counter */}
          <AdmissionCounter />

          {/* Pricing Card + Share & Earn */}
          <div className="flex flex-col md:flex-row flex-wrap items-start justify-center gap-6 max-w-[1000px] mx-auto mt-10 mb-16 px-5">
          <div className="flex-1 basis-[420px] max-w-[480px] mx-auto" data-testid="plan-card-lifetime">
            {admissionsClosed ? (
              <Card className="border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20">
                <CardContent className="pt-6 text-center flex flex-col justify-center">
                  <h3 className="font-bold text-red-600 dark:text-red-400 mb-1">
                    {t('pricing_admissions_closed', 'Admissions Closed')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('pricing_join_waitlist_desc', 'Premium seats are full. Join the waitlist for the next opening.')}
                  </p>
                  {waitlistJoined ? (
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                      {t('pricing_waitlist_joined', "You're on the list!")}
                    </p>
                  ) : (
                    <form onSubmit={handleJoinWaitlist} className="space-y-2">
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="email"
                          required
                          placeholder="you@example.com"
                          value={waitlistEmail}
                          onChange={(e) => setWaitlistEmail(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <Button type="submit" className="w-full rounded-full" disabled={waitlistSubmitting}>
                        {waitlistSubmitting ? t('pricing_joining', 'Joining...') : t('pricing_join_waitlist', 'Join Waitlist')}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            ) : (
              <PricingPlanCard
                plans={plans}
                selectedPlanId={planId}
                onSelectPlan={setSelectedPlanId}
                lifetimeBadge={settings?.lifetime_badge_text || 'BEST VALUE'}
                headerExtra={switchableCountries.length > 1 && (
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="text-xs border rounded-full px-2.5 py-1 bg-background text-muted-foreground cursor-pointer"
                    aria-label={t('pricing_country_switcher', 'Change country')}
                    data-testid="country-switcher"
                  >
                    {switchableCountries.map((c) => (
                      <option key={c.country_code} value={c.country_code}>
                        {c.country_name} ({c.currency_code})
                      </option>
                    ))}
                  </select>
                )}
                price={finalPrice}
                originalPrice={hasDiscount ? lifetimePrice : lifetimeOriginalPrice}
                taxRate={gstPercentage}
                taxLabel={taxLabel}
                total={gstInclusiveTotal}
                isIndia={isIndia}
                formatPrice={formatPrice}
                beforeFeatures={
                  <>
                  {seatsLow && (
                    <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-6">
                      {tFormat(
                        'pricing_seats_left',
                        { count: admissionInfo.remaining },
                        'Only {count} seats left!'
                      )}
                    </p>
                  )}

                  {/* Coupon(s) — up to 2 can be stacked, India-only. The stacked-
                      discount preview (/payments/coupons/validate-multiple) only
                      computes against the INR settings price, so it would show the
                      wrong (INR-based) numbers for a non-India price. Non-India
                      countries get the simpler single-code input below instead —
                      no preview, but the code is passed through to Stripe/PayPal
                      checkout, which validates it against that country's own base
                      price server-side (routes/payments.py). */}
                  {settings?.enable_coupons && isIndia && (
                    <div className="mb-6">
                      {appliedCoupons.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {appliedCoupons.map((code) => (
                            <div
                              key={code}
                              className="flex items-center gap-1.5 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-full pl-3 pr-2 py-1 text-sm font-semibold text-green-700 dark:text-green-300"
                            >
                              <Check className="w-3.5 h-3.5" /> {code}
                              <button
                                type="button"
                                onClick={() => handleRemoveCoupon(code)}
                                className="text-muted-foreground hover:text-foreground leading-none text-base"
                                aria-label={`Remove ${code}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {appliedCoupons.length < 2 && (
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder={
                                appliedCoupons.length === 0
                                  ? t('pricing_coupon_ph', 'Enter coupon code')
                                  : t('pricing_coupon_ph_second', 'Enter second coupon code (optional)')
                              }
                              value={couponInput}
                              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddCoupon()}
                              className="pl-10"
                              data-testid="coupon-input"
                            />
                          </div>
                          <Button
                            variant="outline"
                            onClick={handleAddCoupon}
                            disabled={validatingCoupon}
                            data-testid="apply-coupon-btn"
                          >
                            {validatingCoupon
                              ? t('pricing_checking', 'Checking...')
                              : t('pricing_apply_btn', 'Apply')}
                          </Button>
                        </div>
                      )}

                      {couponResult?.valid && (
                        <div className="mt-2 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg space-y-1">
                          {couponResult.coupons.map((c) => (
                            <div
                              key={c.code}
                              className="flex justify-between text-sm text-green-700 dark:text-green-300"
                            >
                              <span>
                                {c.code} ({c.discount_type === 'percent' ? `${c.discount_value}%` : formatPrice(c.discount_value)} off)
                              </span>
                              <span>-{formatPrice(c.discount_amount)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm font-bold text-green-700 dark:text-green-300 border-t border-green-200 dark:border-green-800 pt-1 mt-1">
                            <span>{t('pricing_total_savings', 'Total savings')}</span>
                            <span>-{formatPrice(couponResult.total_discount)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {settings?.enable_coupons && !isIndia && (
                    <div className="mb-6">
                      <div className="relative">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder={t('pricing_coupon_ph', 'Enter coupon code')}
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          className="pl-10"
                          data-testid="coupon-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {t(
                          'pricing_coupon_checkout_note',
                          "We'll apply this coupon and show your discount at checkout."
                        )}
                      </p>
                    </div>
                  )}
                  </>
                }
              >

                  {(() => {
                    // Gateway button visibility now requires BOTH the admin having
                    // actually enabled+configured that gateway in Integrations
                    // settings (gatewayConfig, same source Razorpay always used —
                    // Stripe/PayPal previously ignored this and showed regardless,
                    // which is why they 503'd when clicked) AND that country's
                    // `payment_gateways` list allowing it (see country_pricing.py).
                    // Before availableCountries/countryPricing load, default to
                    // "allowed" so the buttons don't flash hidden-then-shown.
                    const availableGateways = countryPricing?.payment_gateways
                      || ['razorpay', 'stripe', 'paypal'];
                    const razorpayEnabled = !!gatewayConfig?.razorpay?.enabled
                      && availableGateways.includes('razorpay');
                    const stripeEnabled = !!gatewayConfig?.stripe?.enabled
                      && availableGateways.includes('stripe');
                    const paypalEnabled = !!gatewayConfig?.paypal?.enabled
                      && availableGateways.includes('paypal');

                    if (!razorpayEnabled && !stripeEnabled && !paypalEnabled) {
                      const supportEmail = settings?.contact_email || 'support@thachari.com';
                      const waMessage = encodeURIComponent("Hi, I'm interested in enrolling — online payment isn't set up for my country yet.");
                      return (
                        <div className="text-center p-6 bg-[#fdf8f4] dark:bg-[#1a1410] border-2 border-[#e8d5c0] dark:border-[#3a2f22] rounded-xl">
                          <div className="text-4xl mb-3">💬</div>
                          <h3 className="font-bold mb-2">
                            {t('pricing_contact_to_enroll_title', 'Contact Us to Enroll')}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto">
                            {t(
                              'pricing_contact_to_purchase',
                              "Online payment isn't set up for your country yet. Reach us on WhatsApp to complete your enrollment."
                            )}
                          </p>
                          <div className="flex flex-col gap-2 items-center">
                            <a
                              href={`https://wa.me/919626722220?text=${waMessage}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity"
                            >
                              <WhatsAppIcon className="w-4 h-4" /> WhatsApp: +91 96267 22220
                            </a>
                            <a
                              href={`https://wa.me/919626822220?text=${waMessage}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity"
                            >
                              <WhatsAppIcon className="w-4 h-4" /> WhatsApp: +91 96268 22220
                            </a>
                            <a href={`mailto:${supportEmail}`} className="text-xs text-muted-foreground hover:text-foreground mt-1 underline">
                              {t('pricing_or_email', 'or email')} {supportEmail}
                            </a>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-3">
                        <div className="rounded-lg border bg-muted/30 p-3">
                          <LegalAcceptCheckbox
                            checked={acceptedTerms}
                            onChange={(v) => {
                              setAcceptedTerms(v);
                              if (v) setTermsError(false);
                            }}
                            error={termsError}
                            testId="checkout-accept-terms"
                          />
                        </div>
                        {razorpayEnabled && (
                          <div>
                            <Button
                              className="w-full rounded-full bg-[#A67C52] hover:bg-[#8f6a45] text-white"
                              onClick={handleRazorpayPayment}
                              disabled={processingPayment}
                              data-testid="razorpay-lifetime-btn"
                            >
                              <CreditCard className="w-4 h-4 mr-2" />
                              {processingPayment
                                ? t('pricing_processing_btn', 'Processing...')
                                : isDatedPlan
                                  ? tFormat('pricing_pay_plan_btn', { total: formatPrice(gstInclusiveTotal), name: selectedPlan.name }, 'Pay {total} — {name}')
                                  : t('pricing_get_lifetime_btn', 'Get Premium Access')}
                            </Button>
                            {gatewayConfig?.razorpay?.mode === 'test' && (
                              <p className="text-center text-xs text-amber-600 mt-1.5">
                                {t('pricing_razorpay_test_mode', '🧪 Test mode — no real payment will be made')}
                              </p>
                            )}
                          </div>
                        )}
                        {stripeEnabled && (
                          <Button
                            className={
                              razorpayEnabled
                                ? 'w-full rounded-full'
                                : 'w-full rounded-full bg-[#A67C52] hover:bg-[#8f6a45] text-white'
                            }
                            variant={razorpayEnabled ? 'outline' : undefined}
                            onClick={() => handlePayment('stripe')}
                            disabled={processingPayment}
                            data-testid="stripe-lifetime-btn"
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            {processingPayment
                              ? t('pricing_processing_btn', 'Processing...')
                              : razorpayEnabled
                                ? t('pricing_pay_with_card_btn', 'Pay with Card (Stripe)')
                                : isDatedPlan
                                  ? tFormat('pricing_pay_plan_btn', { total: formatPrice(gstInclusiveTotal), name: selectedPlan.name }, 'Pay {total} — {name}')
                                  : t('pricing_get_lifetime_btn', 'Get Premium Access')}
                          </Button>
                        )}
                        {paypalEnabled && (
                          <Button
                            variant="outline"
                            className="w-full rounded-full"
                            onClick={() => handlePayment('paypal')}
                            disabled={processingPayment}
                            data-testid="paypal-lifetime-btn"
                          >
                            {t('pricing_paypal_label', 'PayPal')}
                          </Button>
                        )}

                        <div className="text-center pt-2">
                          <p className="text-xs text-muted-foreground mb-2">
                            {t('pricing_need_help', 'Need help? Talk to us before buying:')}
                          </p>
                          <div className="flex gap-2 justify-center flex-wrap">
                            <a
                              href="https://wa.me/919626722220"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition-opacity"
                            >
                              <WhatsAppIcon className="w-3.5 h-3.5" /> 96267 22220
                            </a>
                            <a
                              href="https://wa.me/919626822220"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-[#25D366] text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition-opacity"
                            >
                              <WhatsAppIcon className="w-3.5 h-3.5" /> 96268 22220
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
              </PricingPlanCard>
            )}
          </div>

          {/* Share & Earn — non-premium users only (not shown to the isPremium/subscriptionStatus
              branch above, which returns before reaching this point) */}
          {!isPremium && (
            <div className="flex-1 basis-[360px] max-w-[440px] mx-auto">
              <ShareEarnSection user={user} />
            </div>
          )}
          </div>

          {/* Trust badges */}
          <div className="text-center mb-16">
            <p className="text-sm text-muted-foreground mb-4">
              {t('pricing_secure_payments', 'Secure payment powered by')}
            </p>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              {gatewayConfig?.razorpay?.enabled && (
                <Badge variant="outline" className="px-4 py-2">
                  <Shield className="w-4 h-4 mr-2" />
                  Razorpay
                </Badge>
              )}
              <Badge variant="outline" className="px-4 py-2">
                <Shield className="w-4 h-4 mr-2" />
                Stripe
              </Badge>
              <Badge variant="outline" className="px-4 py-2">
                <Shield className="w-4 h-4 mr-2" />
                PayPal
              </Badge>
            </div>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-semibold text-center mb-8">
              {t('pricing_faq_title', 'Frequently Asked Questions')}
            </h3>
            <div className="space-y-3">
              {FAQ_ITEMS.map((faq, i) => (
                <div
                  key={i}
                  className="border rounded-xl p-5 cursor-pointer hover:border-[#A67C52]/50 transition-colors"
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                >
                  <div className="flex justify-between items-center font-semibold">
                    {faq.q}
                    <span className="text-[#A67C52] text-xl font-light leading-none">
                      {activeFaq === i ? '−' : '+'}
                    </span>
                  </div>
                  {activeFaq === i && (
                    <p className="text-sm text-muted-foreground mt-3">{faq.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
